import React, { useState } from 'react';
import { Mail, KeyRound, UserPlus, Send, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminClientService } from '../../services/adminClientService';
import { getErrorMessage } from '../../utils/errorMessage';

/**
 * Card riutilizzabili per le azioni sull'account web di un cliente
 * (richiesta utente 23/02/2026). Usate SIA inline in `AdminClientDetailPage`
 * (sezione "Account Web") SIA dentro una modale quando invocate dal menu
 * contestuale di `AdminClientsPage`.
 */

const cardShell = 'rounded-xl border bg-white overflow-hidden';
const cardHeader = 'flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]';

// ── Cambio Mail ────────────────────────────────────────────────────────────

export function ChangeEmailCard({
  clientKey, currentEmail, onSuccess,
}: { clientKey: string | number; currentEmail?: string | null; onSuccess?: () => void }) {
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      await adminClientService.changeEmail(clientKey, newEmail.trim());
      toast.success('Email aggiornata con successo');
      setNewEmail('');
      onSuccess?.();
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Errore durante l'aggiornamento dell'email"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardShell} data-testid="client-card-change-email">
      <div className={cardHeader}>
        <Mail className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h3 className="text-sm font-semibold">Cambio Mail</h3>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Email attuale</span>
          <p className="text-sm mt-0.5" data-testid="client-current-email">{currentEmail || '—'}</p>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Nuova email</label>
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="nuova.email@esempio.it"
            className="w-full h-9 mt-1 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20"
            disabled={saving}
            data-testid="client-new-email-input"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !newEmail.trim()}
          className="w-full h-9 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="client-change-email-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Aggiorna Email
        </button>
      </div>
    </div>
  );
}

// ── Reset Credenziali ──────────────────────────────────────────────────────

export function ResetCredentialsCard({
  clientKey, email, onSuccess,
}: { clientKey: string | number; email?: string | null; onSuccess?: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      await adminClientService.resetPassword(clientKey);
      toast.success('Link per reimpostare la password inviato via email');
      setConfirming(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Errore durante l'invio del link"));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className={cardShell} data-testid="client-card-reset-credentials">
      <div className={cardHeader}>
        <KeyRound className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h3 className="text-sm font-semibold">Reset Credenziali</h3>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Il link per reimpostare la password verrà inviato a</span>
          <p className="text-sm mt-0.5">{email || '—'}</p>
        </div>
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="w-full h-9 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
            data-testid="client-reset-credentials-open-confirm"
          >
            <KeyRound className="w-4 h-4" /> Invia link di reset
          </button>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="client-reset-credentials-confirm-box">
            <p className="text-xs text-amber-800 flex items-start gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Confermi l'invio del link? Il cliente riceverà per email un link valido 60 minuti per scegliere una nuova password; quella attuale resta valida finché non lo usa.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 h-8 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid="client-reset-credentials-confirm-btn"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Conferma e invia
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={resetting}
                className="h-8 px-3 rounded-lg border text-xs font-medium"
                data-testid="client-reset-credentials-cancel-btn"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Crea Utenza ────────────────────────────────────────────────────────────

export function CreateAccountCard({
  anConto, defaultEmail, onSuccess,
}: { anConto: number; defaultEmail?: string | null; onSuccess?: () => void }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const handleCreate = async () => {
    if (!email.trim()) return;
    setCreating(true);
    try {
      await adminClientService.createAccount(anConto, email.trim());
      toast.success('Utenza creata con successo. Credenziali inviate via email.');
      setCreated(true);
      // Delay la callback di chiusura/reload per lasciare visibile la card
      // di conferma (con l'email di destinazione delle credenziali) prima
      // che il modale genitore si chiuda (segnalato da testing agent, iteration_174).
      setTimeout(() => onSuccess?.(), 1800);
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Errore durante la creazione dell'utenza"));
    } finally {
      setCreating(false);
    }
  };

  if (created) {
    return (
      <div className={cardShell} data-testid="client-card-create-account">
        <div className="p-6 flex flex-col items-center text-center gap-2">
          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          <p className="text-sm font-medium">Utenza creata con successo</p>
          <p className="text-xs text-muted-foreground">Email e password di accesso sono state inviate a {email}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardShell} data-testid="client-card-create-account">
      <div className={cardHeader}>
        <UserPlus className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h3 className="text-sm font-semibold">Crea Utenza</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Questo cliente non ha ancora un account sul portale B2B.
        </p>
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Email (usata per il login)</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="cliente@esempio.it"
            className="w-full h-9 mt-1 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20"
            disabled={creating}
            data-testid="client-create-account-email-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Precompilata con l'email di contatto del cliente; puoi modificarla.</p>
        </div>
        <button
          onClick={handleCreate}
          disabled={creating || !email.trim()}
          className="w-full h-9 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="client-create-account-submit"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          Accetta e Crea Utenza
        </button>
      </div>
    </div>
  );
}
